import React, {useMemo, useState} from "react";
import useRouter from "../../../../../hooks/use-router";
import {useTranslation} from "react-i18next";
import {DefaultInitialPaginationInfo, TableCellAlignments, TableCellTypes} from "../../../../../../core/constants/enums";
import {routeFunctions} from "../../../../../routes";
import {ReactComponent as BarcodeIcon} from "../../../../../../assets/images/barcode-icon.svg";
import OrderListSearchSection from "../../../../../components/search-sections/orders";
import NoEntries from "../../../../../components/app-specific/no-entries";
import Table from "../../../../../containers/table";
import {dialogActions} from "../../../../../../redux/entities/dialog-slice";
import {useDispatch} from "react-redux";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import useSearchData from "../../../../../hooks/use-search-data";
import {SalesOrderListQueryParams} from "../../../../../../core/constants/query-params";
import QueryManagementUtils from "../../../../../../core/services/query-management-utils";

const initialPaginationInfo = {
    ...DefaultInitialPaginationInfo,
    pageSize: 10,
}

const searchStatus = [
    {
        id: 1,
        title: 'Active'
    },
    {
        id: 2,
        title: 'DeActive'
    }
]

// Keys for the users table
const tableCellKeys = {
    code: 'code',
    customerName: 'customerName',
    orderDate: 'orderDate',
    requiredDate: 'requiredDate',
    totalProducts: 'totalProducts',
    totalItems: 'totalItems',
    status: 'status'
}

const PurchaseOrderListView = () => {
    const {query, location, history, stringifyUrl, params} = useRouter();
    const [loading, setLoading] = useState(true);
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [statuses, setStatuses] = useState(searchStatus);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const isMounted = useIsMounted();

    const translation = t("views.panel.company.purchaseOrder.list", {returnObjects: true});

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    
    /**
     * Gets the list of all purchase orders from server:
     * - Calls the api regarding this action, if the api call was successful, populates the [purchaseOrders] in the state
     */
    const search = async () => {
        await delay(2000);
        const forApi = prepareRequestDs();
        const response = {
            resultFlag: true,
            data: {
                items: [
                    {
                        id: 1,
                        code: "Q000123-0",
                        customerName: "Testing",
                        orderDate: new Date(),
                        requiredDate: new Date(),
                        totalProducts: 10,
                        totalItems: 50,
                        status: {
                            id: 1,
                            title: "Open"
                        }
                    },
                    {
                        id: 2,
                        code: "Q000123-0",
                        customerName: "Testing",
                        orderDate: new Date(),
                        requiredDate: new Date(),
                        totalProducts: 10,
                        totalItems: 50,
                        status: {
                            id: 2,
                            title: "Close"
                        }
                    },
                    {
                        id: 3,
                        code: "Q000123-0",
                        customerName: "Testing",
                        orderDate: new Date(),
                        requiredDate: new Date(),
                        totalProducts: 10,
                        totalItems: 50,
                        status: {
                            id: 1,
                            title: "Open"
                        }
                    },
                    {
                        id: 4,
                        code: "Q000123-0",
                        customerName: "Testing",
                        orderDate: new Date(),
                        requiredDate: new Date(),
                        totalProducts: 10,
                        totalItems: 50,
                        status: {
                            id: 2,
                            title: "Close"
                        }
                    },
                    {
                        id: 5,
                        code: "Q000123-0",
                        customerName: "Testing",
                        orderDate: new Date(),
                        requiredDate: new Date(),
                        totalProducts: 10,
                        totalItems: 50,
                        status: {
                            id: 1,
                            title: "Open"
                        }
                    },
                ],
                paginationInfo: {
                    length: 5,
                }
            }
        };
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPurchaseOrders(response?.data?.items);
            const newPaginationInfo = {
                ...paginationInfo,
                length: response?.data.paginationInfo.length ?? 0
            }
            QueryManagementUtils.setPaginationInfo(newPaginationInfo, query, history, location);
        }
        setLoading(false);
    }

    const {paginationInfo, filters, orderBy} = useSearchData(
        SalesOrderListQueryParams,
        search,
        {
            useFilters: true,
            initialFilters: {},
            filtersQueryParamMap: {
                keyword: SalesOrderListQueryParams.keyword,
                status: SalesOrderListQueryParams.status,
                fromDate: SalesOrderListQueryParams.fromDate,
                toDate: SalesOrderListQueryParams.toDate
            },
            usePaginationInfo: true,
            initialPaginationInfo: initialPaginationInfo,

            useOrderBy: true,
            initialOrderBy: undefined,
        }
    );

    /**
     * Prepars the api call request body based on the filter, pagination, and orderby
     * @return {{orderBy: *, paginationInfo: *, filters: {}}}
     */
    const prepareRequestDs = () => {
        return {
            paginationInfo: paginationInfo,
            filters: filters,
            orderBy: orderBy
        }
    }

    /**
     * Clears the form values
     */
    const clearFilters = () => {
        history.push(stringifyUrl({
            url: location.pathname,
            query: {}
        }));
    }

    /**
     * Handles click on the barcode icon button:
     * - Opens dialog for showing the barcode
     * @param {string} code for generating barcode from
     */
    const onBarcodeClicked = (code) => {
        dispatch(dialogActions.configBarcodeDialog({
            open: true,
            onClose: () => dispatch(dialogActions.barcodeDialogToggle(false)),
            barcodeCode: code,
        }));
    }

    /**
     * Handles click on the purchase order's order number:
     * - Navigates user to purchase order single view
     * @param purchaseOrder
     */
    const onSalesOrderSelected = (purchaseOrder) => {
        history.push(routeFunctions.panel.company.purchaseOrders.single(params.company, purchaseOrder.id));
    }

    /**
     * Memo version of table columns
     * @type {object[]}
     */
    const tableColumns = useMemo(() => [
        {
            title: translation.tableHeaders.code ?? '',
            alignment: TableCellAlignments.left,
            type: TableCellTypes.element,
            name: tableCellKeys.code,
            sortable: true
        },
        {
            title: translation.tableHeaders.customerName ?? '',
            alignment: TableCellAlignments.left,
            type: TableCellTypes.string,
            name: tableCellKeys.customerName,
            size: 1.2,
            sortable: true
        },
        {
            title: translation.tableHeaders.orderDate ?? '',
            alignment: TableCellAlignments.right,
            type: TableCellTypes.date,
            name: tableCellKeys.orderDate,
            size: 1.2,
            sortable: true
        },
        {
            title: translation.tableHeaders.requiredDate ?? '',
            alignment: TableCellAlignments.right,
            type: TableCellTypes.date,
            name: tableCellKeys.requiredDate,
            size: 1.2,
            sortable: true
        },
        {
            title: translation.tableHeaders.totalProducts ?? '',
            alignment: TableCellAlignments.right,
            type: TableCellTypes.string,
            name: tableCellKeys.totalProducts,
            size: 1.2,
            sortable: true
        },
        {
            title: translation.tableHeaders.totalItems ?? '',
            alignment: TableCellAlignments.right,
            type: TableCellTypes.string,
            name: tableCellKeys.totalItems,
            sortable: true
        },
        {
            title: translation.tableHeaders.status ?? '',
            alignment: TableCellAlignments.left,
            type: TableCellTypes.string,
            name: tableCellKeys.status,
            sortable: true
        },
    ], [])

    /**
     * Memo version of table rows records
     * @type {object[]}
     */
    const tableRows = useMemo(() => {
        return purchaseOrders?.map(purchaseOrder => ({
            key: purchaseOrder?.id,
            [tableCellKeys.code]: () => (
                <div className={'d-flex flex-row align-items-center'}>
                    {
                        purchaseOrder?.code?.length > 0 &&
                        <div
                            onClick={() => onBarcodeClicked(purchaseOrder?.code)}
                            className={'barcode-icon-button'}
                        >
                            <BarcodeIcon/>
                        </div>
                    }
                    <p
                        className={'text-button'}
                        onClick={() => onSalesOrderSelected(purchaseOrder)}
                    >
                        {purchaseOrder.code}
                    </p>
                </div>
            ),
            [tableCellKeys.customerName]: purchaseOrder?.customerName,
            [tableCellKeys.orderDate]: purchaseOrder?.orderDate,
            [tableCellKeys.requiredDate]: purchaseOrder?.requiredDate,
            [tableCellKeys.totalProducts]: purchaseOrder?.totalProducts,
            [tableCellKeys.totalItems]: purchaseOrder?.totalItems,
            [tableCellKeys.status]: purchaseOrder?.status?.title,
        }))
    }, [purchaseOrders])

    return (
        <>
            <div className={"sales-order-list"}>
                <div className={'panel-card p-0 pt-4'}>
                    <div className={'px-4 d-flex align-items-center justify-content-start'}>
                        <p className={'title'}>
                            {translation?.purchaseOrders ?? ''}
                        </p>
                    </div>
                    <OrderListSearchSection filters={filters} statuses={statuses}/>
                    <div className={'pb-5 px-4'}>
                        {
                            purchaseOrders?.length < 1 && !loading
                                ? <NoEntries
                                    text={
                                        Object.entries(filters ?? {})?.length > 0
                                            ? translation?.empty?.withFilter
                                            : translation?.empty?.text
                                    }
                                    includeButton={Object.entries(filters ?? {})?.length > 0}
                                    buttonProps={{
                                        onClick: clearFilters,
                                        className: 'button primary px-3'
                                    }}/>
                                : <Table
                                    loading={{state: loading, count: 5}}
                                    className={'my-3'}
                                    orderBy={orderBy}
                                    color={'primary'}
                                    onSort={(orderBy) => QueryManagementUtils.setOrderBy(
                                        orderBy,
                                        query,
                                        history,
                                        location,
                                    )}
                                    cells={tableColumns}
                                    data={tableRows}
                                    paginationInfo={paginationInfo}
                                    onPageSizeChange={(p, i) => QueryManagementUtils.setPaginationInfo(
                                        {...p, pageSize: i},
                                        query,
                                        history,
                                        location
                                    )}
                                    onCurrentPageChange={(p, newP) => QueryManagementUtils.setPaginationInfo(
                                        {...p, currentPage: newP},
                                        query,
                                        history,
                                        location,
                                    )}
                                />

                        }
                    </div>
                </div>
            </div>
        </>
    );
}

export default PurchaseOrderListView;
